import {Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, Signal, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SwiperOptions} from 'swiper/types';
import {Autoplay, Pagination} from 'swiper/modules';
import {NgOptimizedImage} from '@angular/common';
import {LucideAngularModule, Search, UserRoundPlus} from 'lucide-angular';
import {Router, RouterLink} from '@angular/router';
import {FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {HairstylesAutocomplete} from '../../shared/components/hairstyles-autocomplete/hairstyles-autocomplete';
import {HairstylesService} from '../../shared/services/hairstyles/hairstyles.service';
import {PlacesAutocomplete} from '../../shared/components/places-autocomplete/places-autocomplete';
import {HowItWorks} from './components/how-it-works/how-it-works';
import {WhyChooseUs} from './components/why-choose-us/why-choose-us';
import {DownloadApp} from './components/download-app/download-app';
import {AdBannerComponent} from './components/ad-banner/ad-banner';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {MetasService} from "../../core/services/meta-service/metas.service";
import {IHairstyle} from "../../shared/interfaces/hairstyle.interface";
import {IMetaData} from "../../core/interfaces/meta-data.interface";
import {SwiperDirective} from "../../shared/directives/swiper/swiper.directive";

interface ISearchForm {
  address: FormControl<string>;
  hairstyle: FormControl<string>;
}

@Component({
  selector: 'app-home',
  imports: [
    NgOptimizedImage,
    LucideAngularModule,
    RouterLink,
    ReactiveFormsModule,
    HairstylesAutocomplete,
    PlacesAutocomplete,
    HowItWorks,
    WhyChooseUs,
    DownloadApp,
    AdBannerComponent,
    TranslatePipe,
    SwiperDirective,
    PlacesAutocomplete,
  ],
  templateUrl: './home.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class Home implements OnInit {
  #router: Router = inject(Router);
  #translateService: TranslateService = inject(TranslateService);
  #metasService: MetasService = inject(MetasService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  readonly hairstyles: Signal<IHairstyle[]> = inject(HairstylesService).hairstyles;
  readonly swiperHeroOptions: Signal<SwiperOptions> = signal<SwiperOptions>({
    modules: [Autoplay, Pagination],
    effect: 'fade',
    pagination: {
      el: '.swiper-pagination',
      clickable: true
    },
    loop: true,
    autoplay: {
      delay: 3500,
      disableOnInteraction: false
    }
  });
  readonly SWIPER_IMAGES: Signal<{ src: string, alt: string }[]> = signal([
    {src: 'images/flat-iron-straightening-natural-hair.webp', alt: 'HOME.HERO.IMAGES.SLIDE1'},
    {src: 'images/smiling-woman-holding-afro-picks.webp', alt: 'HOME.HERO.IMAGES.SLIDE2'},
    {src: 'images/hairstylist-trimming-curly-hair-scissors.webp', alt: 'HOME.HERO.IMAGES.SLIDE3'}
  ]);
  searchForm: FormGroup<ISearchForm> = inject(NonNullableFormBuilder).group<ISearchForm>({
    address: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(200)]
    }),
    hairstyle: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(100)]
    }),
  });

  ngOnInit(): void {
    this.updateSeoMetaTags();
  }

  onSubmitForm(): void {
    this.searchForm.markAllAsTouched();
    if (this.searchForm.invalid) {
      return;
    }
    const {address, hairstyle} = this.searchForm.getRawValue();
    this.#router.navigate(['/hairdressers'], {
      queryParams: {
        address: address.trim(),
        hairstyle: hairstyle.trim()
      }
    }).then();
  }

  private updateSeoMetaTags(): void {
    this.#translateService.get([
      'SEO.HOME.TITLE',
      'SEO.HOME.DESCRIPTION',
      'SEO.HOME.KEYWORDS'
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((translations) => {
        const metaData: IMetaData = {
          title: translations['SEO.HOME.TITLE'],
          description: translations['SEO.HOME.DESCRIPTION'],
          keywords: translations['SEO.HOME.KEYWORDS'],
          url: '/',
          image: '/images/smiling-woman-holding-afro-picks.webp',
          type: 'website',
        };
        this.#metasService.updateMetaTags(metaData).then();
      });
  }

  protected readonly Search = Search;
  protected readonly UserRoundPlus = UserRoundPlus;
}
